import { NOTIFY_SCHEDULE, type GuestKind, type MyGatheringItem } from '@otryad/shared';

import { formatTime } from './format';
import { fmt, plural, PLURAL_SEATS_UPTO, texts } from './texts';

const t = texts.my;

/** Метка вида записи: свой отряд, замена, пробный, разовое место; сбор ops — «Доп. сбор» (раунд персон 1). */
export function relationLabel(item: Pick<MyGatheringItem, 'relation' | 'isOpsExtra'>): string {
  if (item.relation === 'SQUAD') return item.isOpsExtra ? t.opsExtra : t.relation.SQUAD;
  return item.isOpsExtra
    ? `${t.relation[item.relation]} · ${t.opsExtra}`
    : t.relation[item.relation];
}

/** Заметка формата турнира: «Турнир до конца: до 15:00» / «На выбывание: вылетели — можно уходить». */
export function formatNoteOf(
  item: Pick<MyGatheringItem, 'tournamentFormat' | 'startsAt' | 'durationMin' | 'timezone'>,
): string | null {
  if (item.tournamentFormat === 'KNOCKOUT') return t.formatKnockout;
  if (item.tournamentFormat === 'STANDARD') {
    const end = new Date(Date.parse(item.startsAt) + item.durationMin * 60_000);
    return fmt(t.formatStandardUntil, { time: formatTime(end, item.timezone) });
  }
  return null;
}

/**
 * Строка капитана «Разовые: 2 — Борис, Антон» (раунд персон 1, C1): гости сбора не из состава.
 * С видом записи — «Борис (разовое место), Антон (пробный)» для экрана сбора, где строк гостей нет.
 */
export function guestsLine(
  guests: ReadonlyArray<{ firstName: string; kind: GuestKind }>,
  withKind = false,
): string {
  const names = guests.map((g) =>
    withKind ? `${g.firstName} (${t.relation[g.kind].toLocaleLowerCase('ru')})` : g.firstName,
  );
  return fmt(texts.captain.guestsLine, { n: guests.length, names: names.join(', ') });
}

/**
 * «отряд «Северные»» или, без названия, «отряд №3f9a» — хвост id, чтобы два безымянных отряда
 * различались. Раунд персон 2 (M5/N8): активность и время уже стоят в строке записи рядом —
 * «отряд №kepj · падел, ср 21:00» повторял их, поэтому в подписи только то, что отличает отряд.
 */
export function squadLabel(s: { squadName: string | null; squadId: string }): string {
  if (s.squadName) return fmt(t.squadNamed, { name: s.squadName });
  return fmt(t.squadUnnamed, { tail: s.squadId.slice(-4) });
}

/** «11:00–15:00» у турнира (раунд персон 1, M9/C9: сколько держать день), у остальных — начало. */
export function timeText(
  item: Pick<MyGatheringItem, 'isTournament' | 'startsAt' | 'durationMin' | 'timezone'>,
): string {
  const start = formatTime(item.startsAt, item.timezone);
  if (!item.isTournament) return start;
  const end = new Date(Date.parse(item.startsAt) + item.durationMin * 60_000);
  return `${start}–${formatTime(end, item.timezone)}`;
}

/**
 * Ответ на сбор «горит» — бот уже спросил «Идёшь?» (за `rsvpRequestHours` до начала).
 * Раунд персон 3 (C8): красное «Ответь до …» стояло на всех строках журнала, включая сбор через
 * месяц; красным остаётся только то, что требует ответа сейчас.
 */
export function answerUrgent(startsAt: string, now: number = Date.now()): boolean {
  return Date.parse(startsAt) - now <= NOTIFY_SCHEDULE.rsvpRequestHours * 3_600_000;
}

/**
 * Подпись переключателя капитана «Разовые места» с числом мест (раунд персон 2, C5).
 * Раунд персон 3 (C5): выключенный переключатель обещал «до 2 мест» — числа при выключенном нет.
 */
export function singlesToggleLabel(max: number, closed = false): string {
  if (closed || max <= 0) return texts.captain.singlesLabel;
  return fmt(texts.captain.singlesLabelMax, { count: plural(max, PLURAL_SEATS_UPTO) });
}
